import { ResponsivePie } from '@nivo/pie'
import { useMemo, useEffect, useState, useRef } from 'react'
import styles from './GroupedDataPieChart.module.css'
import { moment } from '../utils/dateUtils'

export default function GroupedDataPieChart({ groupedData, groupByFields }) {
    const [currentTheme, setCurrentTheme] = useState('light')
    const [isCopying, setIsCopying] = useState(false)
    const [showCopySuccess, setShowCopySuccess] = useState(false)
    const chartRef = useRef(null)

    // Detect current theme
    useEffect(() => {
        const detectTheme = () => {
            const root = document.documentElement
            const themeAttr = root.getAttribute('data-theme')
            setCurrentTheme(themeAttr || 'light')
        }

        detectTheme()

        // Watch for theme changes
        const observer = new MutationObserver(detectTheme)
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        })

        return () => observer.disconnect()
    }, [])

    // Generate dynamic color palette based on theme (same as DailyTimeChart)
    const getThemeColorPalette = () => {
        switch (currentTheme) {
            case 'dark':
                return [
                    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#F9CA24',
                    '#F0932B', '#EB4D4B', '#6C5CE7', '#A29BFE', '#FD79A8',
                    '#FDCB6E', '#E17055', '#81ECEC', '#74B9FF', '#55A3FF'
                ]
            case 'nightowl':
                return [
                    '#FF5370', '#82AAFF', '#C792EA', '#ADDB67', '#F78C6C',
                    '#FFCB8B', '#C3E88D', '#89DDFF', '#FF9CAC', '#B2CCD6',
                    '#EEFFFF', '#F07178', '#DECB6B', '#DD6B68', '#7E57C2'
                ]
            case 'pastel':
                return [
                    '#FFB3BA', '#BFEFFF', '#FFFFBA', '#BAE1FF', '#FFDFBA',
                    '#BAFFC9', '#FFB3E6', '#E6BAFF', '#BAFFFF', '#C9BAFF',
                    '#FFBABA', '#BAD7FF', '#D1BAFF', '#BAFFBA', '#FFBAF0'
                ]
            case 'gamer':
                return [
                    '#00FF88', '#FF0080', '#00D4FF', '#FFEB3B', '#FF4081',
                    '#00BCD4', '#4CAF50', '#E91E63', '#2196F3', '#9C27B0',
                    '#FF5722', '#795548', '#607D8B', '#FFC107', '#8BC34A'
                ]
            default: // light theme
                return [
                    '#E74C3C', '#3498DB', '#9B59B6', '#27AE60', '#F39C12',
                    '#E67E22', '#1ABC9C', '#34495E', '#2C3E50', '#8E44AD',
                    '#16A085', '#D35400', '#C0392B', '#7F8C8D', '#2980B9'
                ]
        }
    }

    // Generate color for a specific item (consistent with DailyTimeChart)
    const generateColor = (itemName, index) => {
        const palette = getThemeColorPalette()

        // Create a hash from the item name for consistent colors
        let hash = 0
        const str = itemName || `item_${index}`
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i)
            hash = ((hash << 5) - hash) + char
            hash = hash & hash // Convert to 32bit integer
        }

        // Use hash to select color consistently
        const colorIndex = Math.abs(hash) % palette.length
        return palette[colorIndex]
    }

    // Helper to format display names
    const getGroupDisplayName = (groupField, groupKey) => {
        if (!groupKey || groupKey === 'null' || groupKey === 'undefined') {
            return `No ${groupField}`
        }

        // Format different group types
        switch (groupField) {
            case 'date':
                return moment(groupKey).format('MMM D, YYYY')
            case 'week':
                return `Week of ${groupKey}`
            case 'month':
                return moment(groupKey + '-01').format('MMMM YYYY')
            case 'billable':
                return groupKey === 'true' ? 'Billable' : 'Non-billable'
            default:
                return groupKey
        }
    }

    // Helper to calculate total seconds recursively
    const calculateGroupTotalSeconds = (data) => {
        let totalSeconds = 0

        const traverse = (obj) => {
            if (obj && typeof obj === 'object') {
                if (obj.id) {
                    // This is a worklog item
                    totalSeconds += obj.duration_seconds || 0
                } else {
                    // This is a group, traverse its children
                    Object.values(obj).forEach(traverse)
                }
            }
        }

        traverse(data)
        return totalSeconds
    }

    // Format duration for display
    const formatDuration = (seconds) => {
        if (!seconds) return '00:00:00'

        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const remainingSeconds = seconds % 60

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    }

    // Format hours for chart display (more concise)
    const formatHours = (seconds) => {
        const hours = (seconds / 3600).toFixed(1)
        return `${hours}h`
    }

    // Process grouped data for pie chart
    const chartData = useMemo(() => {
        if (!groupedData || Object.keys(groupedData).length === 0) {
            return { data: [], totalSeconds: 0, totalFormatted: '00:00:00' }
        }

        const currentGroupField = groupByFields[0] // Only use top-level grouping

        // Calculate totals for each top-level group
        const groupTotals = Object.entries(groupedData).map(([groupKey, groupValue], index) => {
            const totalSeconds = calculateGroupTotalSeconds(groupValue)
            const displayName = getGroupDisplayName(currentGroupField, groupKey)

            return {
                id: groupKey,
                label: displayName,
                value: totalSeconds,
                valueFormatted: formatDuration(totalSeconds),
                valueHours: formatHours(totalSeconds),
                color: generateColor(displayName, index)
            }
        })

        // Sort by value descending
        groupTotals.sort((a, b) => b.value - a.value)

        // Calculate grand total
        const grandTotalSeconds = groupTotals.reduce((sum, item) => sum + item.value, 0)

        // Convert to hours for pie chart (Nivo expects numeric values)
        const pieData = groupTotals.map(item => ({
            ...item,
            value: item.value / 3600 // Convert seconds to hours for display
        }))

        return {
            data: pieData,
            totalSeconds: grandTotalSeconds,
            totalFormatted: formatDuration(grandTotalSeconds)
        }
    }, [groupedData, groupByFields, currentTheme])

    // Nivo theme configuration
    const theme = {
        background: 'transparent',
        text: {
            fontSize: 11,
            fill: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-family-primary)'
        },
        tooltip: {
            container: {
                background: 'var(--color-bg-primary)',
                color: 'var(--color-text-primary)',
                fontSize: '12px',
                boxShadow: 'var(--shadow-lg)',
                border: '1px solid var(--color-border-light)'
            }
        }
    }

    // Custom tooltip
    const CustomTooltip = ({ datum }) => {
        const percentage = ((datum.value / chartData.data.reduce((sum, d) => sum + d.value, 0)) * 100).toFixed(1)

        return (
            <div className={styles.tooltip}>
                <div className={styles.tooltipHeader}>
                    <div
                        className={styles.tooltipColor}
                        style={{ backgroundColor: datum.color }}
                    />
                    <span className={styles.tooltipLabel}>
                        {datum.label}
                    </span>
                </div>
                <div className={styles.tooltipContent}>
                    <div className={styles.tooltipValue}>
                        {datum.data.valueFormatted}
                    </div>
                    <div className={styles.tooltipPercentage}>
                        {percentage}% of total
                    </div>
                </div>
            </div>
        )
    }

    // Custom center text component for donut chart
    const CenterMetric = ({ centerX, centerY }) => {
        return (
            <g>
                <text
                    x={centerX}
                    y={centerY - 10}
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        fill: 'var(--color-text-secondary)'
                    }}
                >
                    Total
                </text>
                <text
                    x={centerX}
                    y={centerY + 10}
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        fill: 'var(--color-text-primary)',
                        fontFamily: 'var(--font-mono)'
                    }}
                >
                    {chartData.totalFormatted}
                </text>
            </g>
        )
    }

    // Function to copy chart as image
    const copyChartAsImage = async () => {
        if (!chartRef.current || isCopying) return

        setIsCopying(true)

        try {
            // Find the SVG element within the chart wrapper
            const svgElement = chartRef.current.querySelector('svg')
            if (!svgElement) {
                throw new Error('Chart SVG not found')
            }

            // Get the SVG's bounding box for sizing
            const bbox = svgElement.getBoundingClientRect()
            const width = bbox.width
            const height = bbox.height

            // Clone the SVG to avoid modifying the original
            const clonedSvg = svgElement.cloneNode(true)

            // Set explicit dimensions on the cloned SVG
            clonedSvg.setAttribute('width', width)
            clonedSvg.setAttribute('height', height)

            // Get computed styles for background color
            const bgColor = getComputedStyle(document.documentElement)
                .getPropertyValue('--color-bg-secondary').trim() || '#ffffff'

            // Serialize the SVG to a string
            const svgString = new XMLSerializer().serializeToString(clonedSvg)
            const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
            const svgUrl = URL.createObjectURL(svgBlob)

            // Create an image from the SVG
            const img = new Image()
            img.onload = async () => {
                try {
                    // Create a canvas to convert SVG to PNG
                    const canvas = document.createElement('canvas')
                    canvas.width = width * 2 // 2x for better quality
                    canvas.height = height * 2

                    const ctx = canvas.getContext('2d')

                    // Fill background
                    ctx.fillStyle = bgColor
                    ctx.fillRect(0, 0, canvas.width, canvas.height)

                    // Scale context for retina display
                    ctx.scale(2, 2)

                    // Draw the SVG image
                    ctx.drawImage(img, 0, 0, width, height)

                    // Convert canvas to blob
                    canvas.toBlob(async (blob) => {
                        try {
                            // Use Clipboard API to copy the image
                            await navigator.clipboard.write([
                                new ClipboardItem({
                                    'image/png': blob
                                })
                            ])

                            // Show success message
                            setShowCopySuccess(true)
                            setTimeout(() => setShowCopySuccess(false), 2000)
                        } catch (clipboardError) {
                            console.error('Failed to copy to clipboard:', clipboardError)
                            alert('Failed to copy chart. Your browser may not support this feature.')
                        } finally {
                            URL.revokeObjectURL(svgUrl)
                            setIsCopying(false)
                        }
                    }, 'image/png')
                } catch (canvasError) {
                    console.error('Failed to create canvas:', canvasError)
                    URL.revokeObjectURL(svgUrl)
                    setIsCopying(false)
                }
            }

            img.onerror = () => {
                console.error('Failed to load SVG image')
                URL.revokeObjectURL(svgUrl)
                setIsCopying(false)
            }

            img.src = svgUrl
        } catch (error) {
            console.error('Error copying chart:', error)
            setIsCopying(false)
        }
    }

    if (!groupedData || Object.keys(groupedData).length === 0) {
        return (
            <div className={styles.chartContainer}>
                <div className={styles.emptyState}>
                    <p>No data to display</p>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.chartContainer}>
            <div className={styles.chartHeader}>
                <div className={styles.chartHeaderContent}>
                    <div className={styles.chartHeaderText}>
                        <h3>Distribution</h3>
                        <p>Time allocation by {groupByFields[0] || 'category'}</p>
                    </div>
                    <button
                        onClick={copyChartAsImage}
                        className={styles.copyButton}
                        disabled={isCopying}
                        title="Copy chart as image"
                        aria-label="Copy chart as image"
                    >
                        {isCopying ? '⏳' : '📋'}
                    </button>
                </div>
                {showCopySuccess && (
                    <div className={styles.copySuccess}>
                        ✓ Chart copied to clipboard!
                    </div>
                )}
            </div>

            <div className={styles.chartWrapper} ref={chartRef}>
                <ResponsivePie
                    data={chartData.data}
                    margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                    innerRadius={0.6}
                    padAngle={1}
                    cornerRadius={0}
                    activeOuterRadiusOffset={8}
                    colors={{ datum: 'data.color' }}
                    borderWidth={0}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    enableArcLinkLabels={false}
                    arcLabelsSkipAngle={10}
                    arcLabel={(d) => `${((d.value / chartData.data.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}%`}
                    arcLabelsTextColor="white"
                    theme={theme}
                    tooltip={CustomTooltip}
                    layers={['arcs', 'arcLabels', 'arcLinkLabels', 'legends', CenterMetric]}
                    animate={true}
                    motionConfig="gentle"
                />
            </div>

            <div className={styles.legend}>
                {chartData.data.slice(0, 8).map((item) => (
                    <div key={item.id} className={styles.legendItem}>
                        <div
                            className={styles.legendColor}
                            style={{ backgroundColor: item.color }}
                        />
                        <span className={styles.legendLabel}>{item.label}</span>
                        <span className={styles.legendValue}>{item.valueHours}</span>
                    </div>
                ))}
                {chartData.data.length > 8 && (
                    <div className={styles.legendMore}>
                        +{chartData.data.length - 8} more
                    </div>
                )}
            </div>
        </div>
    )
}
